import React, { useMemo, useState } from 'react';

const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 100 * 1024 * 1024;
const MAX_TOTAL_PHOTO_BYTES = 100 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  age: '',
  guardianConsent: false,
  city: '',
  state: '',
  pincode: '',
  craftType: '',
  experienceYears: '',
  priceRange: '',
  website: '',
  about: '',
  originalityConsent: false,
};

function fileKey(file) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function readPhoto(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      fileKey: fileKey(file),
      filename: file.name,
      contentType: file.type,
      dataUrl: String(reader.result || ''),
      size: file.size,
    });
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function formatSize(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
}

export default function SellOnHunarPage() {
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedNumber, setSubmittedNumber] = useState('');
  const totalPhotoBytes = useMemo(() => photos.reduce((total, photo) => total + photo.size, 0), [photos]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const handlePhotoChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const existingKeys = new Set(photos.map((photo) => photo.fileKey));
    const nextFiles = files.filter((file) => !existingKeys.has(fileKey(file)));
    if (photos.length + nextFiles.length > MAX_PHOTOS) {
      setError(`Please upload no more than ${MAX_PHOTOS} photos.`);
      return;
    }
    const tooLarge = nextFiles.find((file) => file.size > MAX_PHOTO_BYTES);
    if (tooLarge) {
      setError(`${tooLarge.name} is larger than ${formatSize(MAX_PHOTO_BYTES)}.`);
      return;
    }
    const nextTotal = totalPhotoBytes + nextFiles.reduce((total, file) => total + file.size, 0);
    if (nextTotal > MAX_TOTAL_PHOTO_BYTES) {
      setError(`All photos together must be ${formatSize(MAX_TOTAL_PHOTO_BYTES)} or less.`);
      return;
    }
    const invalid = nextFiles.find((file) => !ACCEPTED_TYPES.has(file.type));
    if (invalid) {
      setError('Photos must be PNG, JPG, WEBP, or GIF files.');
      return;
    }

    try {
      const parsedPhotos = await Promise.all(nextFiles.map(readPhoto));
      setPhotos((current) => [...current, ...parsedPhotos]);
      setError('');
    } catch (photoError) {
      setError(photoError.message);
    }
  };

  const removePhoto = (photoKey) => {
    setPhotos((current) => current.filter((photo) => photo.fileKey !== photoKey));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setStatus('');
    if (!form.fullName.trim()) {
      setError('Enter your name.');
      return;
    }
    const ageInput = form.age.trim();
    const age = ageInput === '' ? null : Number(ageInput);
    if (age !== null && (!Number.isInteger(age) || age < 13 || age > 120)) {
      setError('If provided, age must be a whole number between 13 and 120.');
      return;
    }
    if (age !== null && age < 18 && !form.guardianConsent) {
      setError('Applicants under 18 need parent or guardian consent.');
      return;
    }
    if (!form.email.trim() || !form.phone.trim() || !form.city.trim() || !form.state.trim() || !/^\d{6}$/.test(form.pincode.trim())) {
      setError('Email, phone, city, state, and a valid 6-digit pincode are required.');
      return;
    }
    if (!form.craftType.trim() || !form.about.trim()) {
      setError('Tell us what you make and a little about your work.');
      return;
    }
    if (photos.length === 0) {
      setError('Upload at least one photo of your work.');
      return;
    }
    if (!form.originalityConsent) {
      setError('Please confirm that the submitted work is yours to sell.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/seller-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, photos }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'Your application could not be sent.');
      setSubmittedNumber(data.applicationNumber || '');
      setStatus('Application sent. The Hunar team will review your work and contact you.');
      setForm(initialForm);
      setPhotos([]);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="seller-page container py-5">
      <header className="seller-page__header">
        <p className="seller-page__eyebrow">Sell on Hunar</p>
        <h1 className="brand-font">Bring your handmade work to more people.</h1>
        <p>
          Hunar works with independent makers who put care into what they create. Share your work and tell us how you would like to sell with us.
        </p>
      </header>

      <section className="seller-page__shell" aria-labelledby="seller-form-title">
        <div className="seller-page__intro">
          <h2 id="seller-form-title">Artist application</h2>
          <p>We review every application before inviting a maker to list products.</p>
        </div>

        {status && (
          <div className="alert alert-success" role="status">
            {status}{submittedNumber && <strong className="d-block mt-1">Application {submittedNumber}</strong>}
          </div>
        )}
        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="seller-form-section">
            <h3>About you</h3>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label" htmlFor="seller-full-name">Name *</label>
                <input id="seller-full-name" className="form-control" value={form.fullName} onChange={(event) => updateField('fullName', event.target.value)} maxLength={160} required />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label" htmlFor="seller-phone">Phone / WhatsApp *</label>
                <input id="seller-phone" className="form-control" type="tel" value={form.phone} onChange={(event) => updateField('phone', event.target.value)} maxLength={30} required />
                <div className="form-text">Please use a phone number connected to your UPI so we can send you profits from your products.</div>
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label" htmlFor="seller-age">Age <span className="text-muted">(optional)</span></label>
                <input id="seller-age" className="form-control" type="number" min="13" max="120" inputMode="numeric" value={form.age} onChange={(event) => updateField('age', event.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label" htmlFor="seller-email">Email *</label>
                <input id="seller-email" className="form-control" type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} maxLength={180} required />
              </div>
              {form.age.trim() !== '' && Number(form.age) >= 13 && Number(form.age) < 18 && (
                <div className="col-12">
                  <label className="form-check">
                    <input className="form-check-input" type="checkbox" checked={form.guardianConsent} onChange={(event) => updateField('guardianConsent', event.target.checked)} />
                    <span className="form-check-label">I have parent or guardian consent to apply to sell on Hunar. *</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="seller-form-section">
            <h3>Where you make</h3>
            <div className="row g-3">
              <div className="col-12 col-md-5">
                <label className="form-label" htmlFor="seller-city">City *</label>
                <input id="seller-city" className="form-control" value={form.city} onChange={(event) => updateField('city', event.target.value)} maxLength={100} required />
              </div>
              <div className="col-12 col-md-5">
                <label className="form-label" htmlFor="seller-state">State *</label>
                <input id="seller-state" className="form-control" value={form.state} onChange={(event) => updateField('state', event.target.value)} maxLength={100} required />
              </div>
              <div className="col-12 col-md-2">
                <label className="form-label" htmlFor="seller-pincode">Pincode *</label>
                <input id="seller-pincode" className="form-control" inputMode="numeric" value={form.pincode} onChange={(event) => updateField('pincode', event.target.value.replace(/\D/g, '').slice(0, 6))} required />
              </div>
            </div>
          </div>

          <div className="seller-form-section">
            <h3>Your work</h3>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label" htmlFor="seller-craft-type">What do you make? *</label>
                <input id="seller-craft-type" className="form-control" placeholder="Crochet flowers, plushies, gifts…" value={form.craftType} onChange={(event) => updateField('craftType', event.target.value)} maxLength={240} required />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label" htmlFor="seller-experience">Years of making</label>
                <input id="seller-experience" className="form-control" type="number" min="0" max="100" value={form.experienceYears} onChange={(event) => updateField('experienceYears', event.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label" htmlFor="seller-price-range">Typical price range</label>
                <input id="seller-price-range" className="form-control" placeholder="₹300 – ₹2,000" value={form.priceRange} onChange={(event) => updateField('priceRange', event.target.value)} maxLength={80} />
              </div>
              <div className="col-12">
                <label className="form-label" htmlFor="seller-about">Tell us about your work *</label>
                <textarea id="seller-about" className="form-control" rows="5" placeholder="What makes your work yours? What would you like to sell through Hunar?" value={form.about} onChange={(event) => updateField('about', event.target.value)} maxLength={2000} required />
              </div>
              <div className="col-12">
                <label className="form-label" htmlFor="seller-website">Instagram or portfolio link</label>
                <input id="seller-website" className="form-control" type="url" placeholder="https://…" value={form.website} onChange={(event) => updateField('website', event.target.value)} maxLength={300} />
              </div>
            </div>
          </div>

          <div className="seller-form-section">
            <h3>Show us what you make *</h3>
            <label className="form-label" htmlFor="seller-photos">Upload photos of your work</label>
            <input id="seller-photos" className="form-control" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handlePhotoChange} />
            <div className="form-text">Up to {MAX_PHOTOS} photos. Each photo can be up to {formatSize(MAX_PHOTO_BYTES)}; total limit {formatSize(MAX_TOTAL_PHOTO_BYTES)}.</div>
            {photos.length > 0 && (
              <div className="seller-photo-grid mt-3">
                {photos.map((photo) => (
                  <div className="seller-photo-preview" key={photo.fileKey}>
                    <img src={photo.dataUrl} alt={`Preview of ${photo.filename}`} />
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removePhoto(photo.fileKey)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="seller-form-consents">
            <label className="form-check">
              <input className="form-check-input" type="checkbox" checked={form.originalityConsent} onChange={(event) => updateField('originalityConsent', event.target.checked)} />
              <span className="form-check-label">I confirm these are my own products or designs and I can sell them. *</span>
            </label>
          </div>

          <button type="submit" className="btn btn-primary-custom rounded-pill px-4" disabled={submitting}>
            {submitting ? 'Sending application…' : 'Apply to sell on Hunar'}
          </button>
        </form>
      </section>
    </main>
  );
}
